import { Spacer } from "@/components/Spacer";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

export default function Section05({ isMobile }: { isMobile: boolean }) {
  return (
    <div className="flex flex-col items-center desktop:py-[100px] desktop:px-[32px] py-[80px] px-[24px] bg-[#1A1A1A]">
      <motion.div
        id="title"
        className="font-pre-semibold text-[#B6BEFF] desktop:text-[20px] text-[14px] leading-[1.2em]"
        style={{ letterSpacing: "-0.02em" }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 80,
          mass: 1,
          delay: 0.1,
        }}
      >
        헬시어가 분석을 끝냈어요!
      </motion.div>
      <Spacer height={isMobile ? 12 : 10} />
      <motion.div
        id="subtitle"
        className="font-pre-bold desktop:text-[32px] text-[24px] leading-[140%] whitespace-pre-line text-center"
        style={{
          letterSpacing: "-0.02em",
          background: "linear-gradient(180deg, #FFF 0%, #999 100%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
        initial={{ opacity: 0, y: 30, scale: 1, rotate: 0, skewX: 0, skewY: 0 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 100,
          mass: 1,
          delay: 0.1,
        }}
      >
        감별결과를 확인하고{"\n"}
        개선 방법을 알아봐요
      </motion.div>
      <Spacer height={isMobile ? 16 : 16} />
      <motion.div
        id="content-01"
        initial={{ opacity: 0, y: 30, scale: 1, rotate: 0, skewX: 0, skewY: 0 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 100,
          mass: 1,
          delay: 0.1,
        }}
      >
        <div
          className="font-pre-regular text-[rgba(237,237,237,0.88)] desktop:text-[20px] text-[16px] leading-[150%] whitespace-pre-line text-center"
          style={{ letterSpacing: "-0.02em" }}
        >
          예상질환과 중증도를 확인하고, 증상을 개선할 수 있는
          {isMobile ? "\n" : ""} 생활수칙을 알아봐요.
          {isMobile ? "" : "\n"}
          증상에 맞는 약과 병원에 가면{isMobile ? "\n" : ""} 어떤 치료를 받는지
          알 수 있어요.
        </div>
        <Spacer height={isMobile ? 40 : 60} />
        <div className="desktop:w-[960px] w-full flex flex-col items-center">
          <img
            src={
              isMobile
                ? "/assets/images/section-05-01-mobile.png"
                : "/assets/images/section-05-01-desktop.png"
            }
            alt="section-05-01"
            className="desktop:w-full desktop:h-[400px] w-[342px] h-[459px]"
          />
        </div>
      </motion.div>
      <Spacer height={32} />
      <motion.div
        id="content-02"
        className="desktop:w-[960px] w-full flex flex-col items-center"
        initial={{ opacity: 0, y: 30, scale: 1, rotate: 0, skewX: 0, skewY: 0 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: isMobile ? 0.0 : 0.5 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 100,
          mass: 1,
          delay: 0.1,
        }}
      >
        <div className="w-full flex desktop:flex-row flex-col items-center justify-between desktop:gap-[16px] gap-[20px]">
          <Card01 isMobile={isMobile} />
          <Card02 isMobile={isMobile} />
          <Card03 isMobile={isMobile} />
        </div>
        <Spacer height={8} />
        <div className="desktop:w-full w-[342px] flex desktop:justify-end justify-start items-start font-pre-regular text-[12px] leading-[150%] text-[#B7B7B7] whitespace-pre-line">
          * 해당 의약품은 처방없이 구매할 수 있으나, 의사 및 약사 등{"\n"}
          전문가와의 상담을 통한 복용을 권해드려요
        </div>
      </motion.div>
    </div>
  );
}

const Tip = ({ title }: { title: string }) => {
  return (
    <div className="bg-[#000000] rounded-[100px] py-[8px] px-[14px] flex items-center justify-center">
      <span
        className="font-pre-medium text-[14px] text-[#ffffff] leading-[140%]"
        style={{ letterSpacing: "-0.3px" }}
      >
        {title}
      </span>
    </div>
  );
};

const Card01 = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <div className="desktop:w-full w-[342px] pt-[24px] flex flex-col items-center bg-[#3F444F] rounded-[12px]">
      <div className="w-full flex flex-col items-start pl-[24px]">
        <Tip title={"생활수칙"} />
        <Spacer height={12} />
        <div
          className="font-pre-bold text-[#ffffff] text-[20px] leading-[140%] whitespace-pre-line"
          style={{ letterSpacing: "-0.3px" }}
        >
          증상을 개선할 수 있는{"\n"}
          생활 속 수칙을 알려줘요
        </div>
      </div>
      <Spacer height={31} />
      <img
        src={
          isMobile
            ? "/assets/images/section-05-02-mobile.png"
            : "/assets/images/section-05-02-desktop.png"
        }
        alt="section-05-02"
        className="desktop:w-[261px] desktop:h-[286.5px] w-[342px] h-[307.5px]"
      />
    </div>
  );
};

const Card02 = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <div className="desktop:w-full w-[342px] pt-[24px] flex flex-col items-center bg-[#E8FAED] rounded-[12px]">
      <div className="w-full flex flex-col items-start pl-[24px]">
        <Tip title={"치료방법"} />
        <Spacer height={12} />
        <div
          className="font-pre-bold text-[#17191C] text-[20px] leading-[140%] whitespace-pre-line"
          style={{ letterSpacing: "-0.3px" }}
        >
          병원에 가면 받게 될{"\n"}
          검사와 치료를 알 수 있어요
        </div>
      </div>
      <Spacer height={31} />
      <img
        src={
          isMobile
            ? "/assets/images/section-05-03-mobile.png"
            : "/assets/images/section-05-03-desktop.png"
        }
        alt="section-05-02"
        className="desktop:w-[261px] desktop:h-[286.5px] w-[342px] h-[307.5px]"
      />
    </div>
  );
};

const Card03 = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <div className="desktop:w-full w-[342px] pt-[24px] flex flex-col items-center bg-[#E2E5FF] rounded-[12px]">
      <div className="w-full flex flex-col items-start pl-[24px]">
        <Tip title={"의약품 추천"} />
        <Spacer height={12} />
        <div
          className="font-pre-bold text-[#17191C] text-[20px] leading-[140%] whitespace-pre-line"
          style={{ letterSpacing: "-0.3px" }}
        >
          예상 질환에 맞는 약의{"\n"}
          복용법과 부작용을 확인해요
        </div>
      </div>
      <Spacer height={31} />
      <img
        src={
          isMobile
            ? "/assets/images/section-05-04-mobile.png"
            : "/assets/images/section-05-04-desktop.png"
        }
        alt="section-05-02"
        className="desktop:w-[309px] desktop:h-[286.5px] w-[342px] h-[307.5px]"
      />
    </div>
  );
};
